import React from "react";
import PropTypes from "prop-types";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import makeStyles from "@mui/styles/makeStyles";
import Chip from "@limitless/modern/data-display/chip/Chip";

import { useTranslation } from "@limitless/common/hooks";
import { PortalButton } from "@limitless/portal";
import { ClientChecksTemplate, CompleteIncompleteIcon } from "@limitless/content";
import { useExpertChecksHook } from "@limitless/expert";
import WithdrawalInfo from "@limitless/icons/InfoCircle";
import LimitlessIconButton from "@limitless/modern/inputs/icon-button/IconButton";
import { Tooltip } from "@mui/material";

const useStyles = makeStyles(theme => ({
    card: {
        width: "330px",
        height: 140,
        borderRadius: 16,
        boxShadow: "0px 3px 8px -4px rgba(0, 0, 0, 0.3)",
        backgroundColor: theme.palette.background.elevation1,
        marginBottom: 24,
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        paddingTop: theme.spacing(2),
        paddingBottom: theme.spacing(1),
        marginLeft: "12px",
        overflow: "hidden",
        position: "relative"
    },
    cardTitle: {
        margin: "24px 32px",
        paddingTop: 2,
        overflowY: "auto",
        maxHeight: "100%",
        wordBreak: "break-word",
        "&::-webkit-scrollbar": {
            display: "none"
        }
    },
    row2Text: {
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        color: theme.palette.primary.main,
        paddingRight: "35px",
        fontWeight: 500,
        paddingBottom: theme.spacing(2),
        marginTop: "auto"
    },
    dialog: {
        borderRadius: 16,
        width: 646
    },
    dialogTitle: {
        padding: "24px 12px 12px 24px"
    },
    chip: {
        backgroundColor: theme.palette.secondary.light,
        minWidth: 57,
        borderRadius: "8px",
        whiteSpace: "nowrap",
        marginBottom: "10px",
        marginLeft: "20px"
    }
}));

const ExpertMemberDetailList = ({ title, checks, readOnly = false }) => {
    const [selectedCheck, setSelectedCheck] = React.useState(null);
    const { t } = useTranslation();
    const classes = useStyles();
    const { saveMemberDetails } = useExpertChecksHook(t);
    const isReadOnly = readOnly || selectedCheck?.readOnly;

    const handleClose = () => setSelectedCheck(null);

    const handleValueChange = (newValue, isInvalid) => {
        setSelectedCheck({ ...selectedCheck, isInvalid, value: newValue });
    };

    const handleSave = () => {
        saveMemberDetails(selectedCheck);
        handleClose();
    };

    return (
        <Grid item>
            <Grid item style={{ display: "flex", alignItems: "center" }}>
                <Chip className={classes.chip} label={t(title)} />
                {title === "Awaiting verification" && (
                    <LimitlessIconButton
                        icon={<WithdrawalInfo />}
                        label={t("Verification is a manual process, please be patient and check back")}
                        style={{ marginLeft: 8, marginTop: "-10px" }}
                    />
                )}
            </Grid>

            <Grid item>
                {checks.map((check, index) => {
                    const { title: checkTitle, description } = check;

                    return (
                        <Grid
                            key={index}
                            item
                            className={classes.card}
                            onClick={() => setSelectedCheck(check)}
                            style={{ cursor: "pointer" }}
                        >
                            <Grid item className={classes.cardTitle}>
                                <Typography color={theme => theme.palette.text.primary} variant="body2">
                                    {checkTitle}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    color={theme => theme.palette.text.primary}
                                    dangerouslySetInnerHTML={{ __html: description }}
                                />
                            </Grid>
                            {check.verification && (
                                <div
                                    style={{
                                        position: "absolute",
                                        bottom: 8,
                                        right: 12,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center"
                                    }}
                                >
                                    <Tooltip
                                        title={check?.verification?.valid === true ? t("Verified") : t("Rejected")}
                                        placement="top"
                                    >
                                        <div>
                                            <CompleteIncompleteIcon
                                                complete={check.verification.valid === true}
                                                error={check.verification.valid === false}
                                            />
                                        </div>
                                    </Tooltip>
                                </div>
                            )}
                        </Grid>
                    );
                })}
            </Grid>

            {selectedCheck && (
                <Dialog onClose={handleClose} open classes={{ paper: classes.dialog }} maxWidth="md">
                    <Grid container className={classes.dialogTitle}>
                        <Grid container item xs={12} justifyContent="flex-end" style={{ marginTop: -16 }}>
                            <IconButton aria-label="close" onClick={handleClose} id="closeCheck" size="large">
                                <CloseIcon />
                            </IconButton>
                        </Grid>
                    </Grid>

                    <Grid container>
                        <Grid item xs={12}>
                            <ClientChecksTemplate
                                readOnly={isReadOnly}
                                check={selectedCheck}
                                onCheckValueChange={handleValueChange}
                            />
                        </Grid>
                        {selectedCheck.verification?.valid === false && (
                            <Grid
                                container
                                style={{ margin: "0px 48px", width: "fit-content" }}
                                wrap="nowrap"
                                alignItems="center"
                            >
                                <CompleteIncompleteIcon complete={false} error />
                                <Typography color="error" variant="subtitle2" style={{ marginLeft: 8 }}>
                                    {selectedCheck.verificationFailedText}
                                </Typography>
                            </Grid>
                        )}
                    </Grid>

                    <Grid container style={{ marginTop: 16, marginBottom: 16 }}>
                        <Grid item xs={4} />
                        <Grid container item xs={4} justifyContent="center">
                            <PortalButton
                                onClick={isReadOnly ? handleClose : handleSave}
                                loading={false}
                                fullWidth
                                disabled={selectedCheck.isInvalid === true}
                            >
                                {t(isReadOnly ? "Close" : "Submit")}
                            </PortalButton>
                        </Grid>
                        <Grid item xs={4} />
                    </Grid>
                </Dialog>
            )}
        </Grid>
    );
};

ExpertMemberDetailList.propTypes = {
    title: PropTypes.string.isRequired,
    checks: PropTypes.array.isRequired,
    readOnly: PropTypes.bool
};

export default ExpertMemberDetailList;
